import React, { useState } from "react";
import {
  Stack,
  Group,
  Box,
  Button,
  Select,
  NumberInput,
  Text,
  Tooltip,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import {
  IconCheck,
  IconX,
  IconInfoCircle,
  IconArrowLeft,
} from "@tabler/icons-react";
import {
  AmountType,
  CommissionRecord,
  CreateCommissionInput,
  CalculationMethod,
  CommissionTierDrafts,
} from "../../types/commission";
import CommissionInput, { parseCommissionValue } from "./CommissionInput";
import TieredCommissionBuilder from "./TieredCommissionBuilder";
import {
  formatCommissionAmount,
  wireToDrafts,
  draftsToWire,
  validateTierDrafts,
} from "../../utils/commissionUtils";

interface CommissionFormProps {
  staffId: string;
  existingCommission?: CommissionRecord | null;
  onSubmit: (data: CreateCommissionInput) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
  /** Hide the "Effective Start Date" field. Used for tier commission templates,
   * which have no date — the date is set to "today" when applied to staff. */
  hideEffectiveDate?: boolean;
  /** Override the primary button label (defaults to Save/Update Commission). */
  submitLabel?: string;
  /** Override the secondary button label (defaults to Cancel/Back). */
  cancelLabel?: string;
  /** Render read-only: disable inputs and hide the submit button. */
  disabled?: boolean;
  /** Hide the secondary (cancel/back) button entirely. */
  hideCancel?: boolean;
  /** Node rendered at the left of the footer row (e.g. a "Remove" action). */
  leftSection?: React.ReactNode;
  /** Disable (but keep visible) the submit button and lock the inputs while a
   * sibling action is in flight (e.g. a "Remove" running in the footer). */
  submitDisabled?: boolean;
}

const CALC_METHOD_OPTIONS = [
  { value: "STANDARD", label: "Standard" },
  { value: "COMMISSION_ONLY", label: "Commission Only" },
  { value: "GREATER_THAN", label: "Greater Than" },
  { value: "NUM_SERVICES", label: "Threshold" },
  { value: "TIERED", label: "Tiered / Step" },
];

const CalcMethodTooltip = () => (
  <Stack gap={8}>
    <Text size="sm" c="white">
      <Text size="sm" span fw={600} c="white">
        Standard:
      </Text>{" "}
      Applies commissions on all service and product transactions.
    </Text>
    <Text size="sm" c="white">
      <Text size="sm" span fw={600} c="white">
        Commission Only:
      </Text>{" "}
      Staff earn only commission on services and products, with no base hourly
      pay.
    </Text>
    <Text size="sm" c="white">
      <Text size="sm" span fw={600} c="white">
        Greater Than:
      </Text>{" "}
      Commissions apply only when they exceed the staff member's hourly rate.
    </Text>
    <Text size="sm" c="white">
      <Text size="sm" span fw={600} c="white">
        Threshold:
      </Text>{" "}
      Calculated on a daily basis. Staff are paid hourly if they service under{" "}
      <i>X</i> number of services that day. Otherwise, they are paid on
      commission.
    </Text>
    <Text size="sm" c="white">
      <Text size="sm" span fw={600} c="white">
        Tiered / Step:
      </Text>{" "}
      Staff earn escalating rates as their period earnings grow. Once a category
      total reaches a tier, that tier's rate applies to the entire category
      total.
    </Text>
  </Stack>
);

const formatDateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDateString = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Convert a CommissionRecord's amount fields to a display string for CommissionInput.
 * e.g. { amount: 15, type: "Percentage" } → "15%", { amount: 25, type: "Fixed Amount" } → "$25"
 */
const toDisplayValue = (
  amount: number | null,
  amountType: string | null,
): string => {
  if (amount === null || amount === undefined || !amountType) return "";
  return formatCommissionAmount(amount, amountType as AmountType | null) || "";
};

const NewCommissionForm: React.FC<CommissionFormProps> = ({
  staffId,
  existingCommission,
  onSubmit,
  onCancel,
  isLoading,
  hideEffectiveDate = false,
  submitLabel,
  cancelLabel,
  disabled = false,
  hideCancel = false,
  leftSection,
  submitDisabled = false,
}) => {
  // Lock every input while read-only, while a save is in flight, or while a
  // sibling action (e.g. Remove) is running.
  const fieldsDisabled = disabled || isLoading || submitDisabled;
  const [calculationMethod, setCalculationMethod] = useState<CalculationMethod>(
    existingCommission?.calculation_method || "STANDARD",
  );
  const [effectiveStartDate, setEffectiveStartDate] = useState<Date | null>(
    existingCommission?.effective_start_date
      ? parseDateString(existingCommission.effective_start_date)
      : new Date(),
  );
  const [serviceCommission, setServiceCommission] = useState(
    existingCommission
      ? toDisplayValue(
          existingCommission.service_amount,
          existingCommission.service_amount_type,
        )
      : "",
  );
  const [productCommission, setProductCommission] = useState(
    existingCommission
      ? toDisplayValue(
          existingCommission.product_amount,
          existingCommission.product_amount_type,
        )
      : "",
  );
  const [numServicesCutoff, setNumServicesCutoff] = useState<number | "">(
    existingCommission?.num_services_cutoff ?? "",
  );
  const [tierDrafts, setTierDrafts] = useState<CommissionTierDrafts>(
    wireToDrafts(existingCommission?.tiers),
  );

  const [serviceError, setServiceError] = useState<string | null>(null);
  const [productError, setProductError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setFormError(null);

    if (calculationMethod === "TIERED") {
      const tierError = validateTierDrafts(tierDrafts);
      if (tierError) {
        setFormError(tierError);
        return;
      }
      if (!hideEffectiveDate && !effectiveStartDate) {
        setFormError("Effective start date is required.");
        return;
      }
      const tieredInput: CreateCommissionInput = {
        staff_id: staffId,
        calculation_method: "TIERED",
        tiers: draftsToWire(tierDrafts),
      };
      if (!hideEffectiveDate && effectiveStartDate) {
        tieredInput.effective_start_date =
          formatDateToString(effectiveStartDate);
      }
      if (existingCommission?.id) {
        tieredInput.id = existingCommission.id;
      }
      await onSubmit(tieredInput);
      return;
    }

    // Validate at least one commission is provided
    const serviceParsed = parseCommissionValue(serviceCommission);
    const productParsed = parseCommissionValue(productCommission);

    if (!serviceParsed && !productParsed) {
      setFormError(
        "At least one of service or product commission is required.",
      );
      return;
    }

    if (serviceError || productError) {
      setFormError("Please fix the commission value errors before saving.");
      return;
    }

    if (!hideEffectiveDate && !effectiveStartDate) {
      setFormError("Effective start date is required.");
      return;
    }

    if (calculationMethod === "NUM_SERVICES") {
      if (numServicesCutoff === "" || numServicesCutoff <= 0) {
        setFormError("Threshold value is required and must be greater than 0.");
        return;
      }
    }

    const input: CreateCommissionInput = {
      staff_id: staffId,
      calculation_method: calculationMethod,
    };

    if (!hideEffectiveDate && effectiveStartDate) {
      input.effective_start_date = formatDateToString(effectiveStartDate);
    }

    if (existingCommission?.id) {
      input.id = existingCommission.id;
    }

    if (serviceParsed) {
      input.service_amount = serviceParsed.amount;
      input.service_amount_type = serviceParsed.amountType;
    }

    if (productParsed) {
      input.product_amount = productParsed.amount;
      input.product_amount_type = productParsed.amountType;
    }

    if (calculationMethod === "NUM_SERVICES" && numServicesCutoff !== "") {
      input.num_services_cutoff = numServicesCutoff;
    }

    await onSubmit(input);
  };

  return (
    <Stack gap="sm">
      <Select
        label={
          <Group gap={4} align="center">
            <Text size="sm" fw={500} c="var(--settings-ink-2)">
              Calculation Method
            </Text>
            <Tooltip
              label={<CalcMethodTooltip />}
              maw={340}
              multiline
              withArrow
              position="bottom"
            >
              <IconInfoCircle
                size={16}
                color="var(--mantine-color-dimmed)"
                style={{ cursor: "help" }}
              />
            </Tooltip>
          </Group>
        }
        data={CALC_METHOD_OPTIONS}
        value={calculationMethod}
        onChange={(val) => {
          const method = (val as CalculationMethod) || "STANDARD";
          setCalculationMethod(method);
          setServiceError(null);
          setProductError(null);
          setFormError(null);
          if (method !== "NUM_SERVICES") {
            setNumServicesCutoff("");
          }
        }}
        allowDeselect={false}
        disabled={fieldsDisabled}
      />

      {calculationMethod === "NUM_SERVICES" && (
        <NumberInput
          label="Threshold (Number of Services)"
          placeholder="e.g. 4"
          min={1}
          value={numServicesCutoff}
          onChange={(val) =>
            setNumServicesCutoff(val === "" ? "" : Number(val))
          }
          required
          disabled={fieldsDisabled}
        />
      )}

      {!hideEffectiveDate && (
        <DateInput
          label="Effective Start Date"
          placeholder="Select date"
          value={effectiveStartDate}
          disabled={fieldsDisabled}
          onChange={(val) => {
            if (!val) {
              setEffectiveStartDate(null);
              return;
            }
            // Mantine DateInput creates dates at UTC midnight. Extract
            // the UTC components to build a local midnight Date so
            // formatDateToString (which uses local getters) stays correct.
            const d = new Date(val);
            setEffectiveStartDate(
              new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
            );
          }}
        />
      )}

      {calculationMethod === "TIERED" ? (
        <TieredCommissionBuilder
          value={tierDrafts}
          onChange={setTierDrafts}
          disabled={fieldsDisabled}
        />
      ) : (
        <>
          <CommissionInput
            label="Service Commission"
            value={serviceCommission}
            onChange={setServiceCommission}
            onError={setServiceError}
            error={serviceError || undefined}
            disabled={fieldsDisabled}
          />

          <CommissionInput
            label="Product Commission"
            value={productCommission}
            onChange={setProductCommission}
            onError={setProductError}
            error={productError || undefined}
            disabled={fieldsDisabled}
          />
        </>
      )}

      {formError && (
        <Text size="sm" c="red">
          {formError}
        </Text>
      )}

      <Group justify="space-between" mt="xs">
        <Box>{leftSection}</Box>
        <Group gap="sm">
          {!hideCancel && (
            <Button
              variant="light"
              color="gray"
              leftSection={
                cancelLabel || existingCommission ? (
                  <IconX size={14} />
                ) : (
                  <IconArrowLeft size={14} />
                )
              }
              onClick={onCancel}
              disabled={isLoading}
              size="sm"
            >
              {cancelLabel ?? (existingCommission ? "Cancel" : "Back")}
            </Button>
          )}
          {!disabled && (
            <Button
              color="var(--brand-teal)"
              leftSection={<IconCheck size={14} />}
              onClick={handleSubmit}
              loading={isLoading}
              disabled={submitDisabled}
              size="sm"
            >
              {submitLabel ??
                (existingCommission ? "Update Commission" : "Save Commission")}
            </Button>
          )}
        </Group>
      </Group>
    </Stack>
  );
};

export default NewCommissionForm;
